import React, { useContext, useEffect, useState } from 'react'
import MediaQuery from 'react-responsive'

import { AcornsIcon } from '@acorns/icons'
import { Heading2, Spinner } from '@acorns/web-components'
import { useRemoteStrings } from '@acorns/web-utils'
import { useQuery } from '@apollo/react-hooks'

import { CTAContainer } from 'components/CTAContainer'
import Container from 'components/Layout'
import Nav from 'components/Nav'
import Separator from 'components/Separator'
import { withClickTrack } from 'components/Trackers/ClickTracker'
import { TRACKER_IDS } from 'components/Trackers/trackerIDs'
import {
  ActionType,
  PageName,
  Portfolio as PortfolioType,
  Suitability_Portfolio,
} from 'generated/types'
import { TestIDs } from 'test/utils/testIds'
import themeValues from 'theme'
import trackEvent from 'utils/analytics/trackEvent'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { FunnelContext } from 'utils/FunnelContext'

import AllPortfolios from '../allPortfolios.gql'
import { ThemeSelectionButtonTapped } from '../events'
import {
  Content,
  CoreIconContainer,
  InfoNoteLink,
  SpinnerContainer,
  StyledBody,
  StyledButton,
  StyledHeading,
  StyledInfoNote,
  StyledMobileCard,
  StyledSustainableMainBody,
  SustainableIconContainer,
  ThemeCardButton,
  ThemeCardTextContainer,
  ThemeContainer,
} from './styled-components'

type AllPortfolioDataQuery = {
  portfolios: PortfolioType[]
  recommendedPortfolio: Suitability_Portfolio
}

export enum PortfolioThemes {
  SUSTAINABLE = 'ESG',
  CORE = 'Core',
}

const SelectPortfolioTheme = (props: WithFlowRouterProps) => {
  const NextButton = withClickTrack<any, any>(TRACKER_IDS.PORTFOLIO_SELECT_THEME.NEXT)(StyledButton)
  const LearnMoreLink = withClickTrack<any, any>(TRACKER_IDS.PORTFOLIO_SELECT_THEME.LEARN_MORE)(
    InfoNoteLink,
  )
  const { isBackEnabled, isDynamicFunnelEnabled, routeToPage } = useContext(FunnelContext)
  const { getRemoteString, StyledRemoteString } = useRemoteStrings()
  const [isCoreCardActive, setIsCoreCardActive] = useState<boolean>(false)
  const [selectedTheme, setSelectedTheme] = useState('')
  const [isSustainableCardActive, setIsSustainableCardActive] = useState<boolean>(false)
  const { data: allPortfoliosData, loading } = useQuery<AllPortfolioDataQuery>(AllPortfolios, {
    fetchPolicy: 'network-only',
  })
  const isSelectedThemeEmpty = selectedTheme === ''
  const isLaterOnboardingFlow = props.flow === 'laterOnboarding'
  const coreDetails = isLaterOnboardingFlow
    ? 'portfolio_select_theme.core_description.later'
    : 'portfolio.theme.core'
  const sustainableDetails = isLaterOnboardingFlow
    ? 'portfolio_select_theme.sustainable_description.later'
    : 'portfolio.theme.sustainable.markdown'

  const handleThemeSelectClick = (cardType: string) => {
    if (!isCoreCardActive && cardType === PortfolioThemes.CORE) {
      setIsCoreCardActive(true)
      setIsSustainableCardActive(false)
      setSelectedTheme(PortfolioThemes.CORE)
      trackEvent('PortfolioThemeTapped', {
        eventData: ThemeSelectionButtonTapped('Core Portfolios', 'core'),
      })
    } else if (!isSustainableCardActive && cardType === PortfolioThemes.SUSTAINABLE) {
      setIsSustainableCardActive(true)
      setIsCoreCardActive(false)
      setSelectedTheme(PortfolioThemes.SUSTAINABLE)
      trackEvent('PortfolioThemeTapped', {
        eventData: ThemeSelectionButtonTapped('ESG Portfolios', 'sustainable'),
      })
    }
  }
  const handleContinue = () => {
    localStorage.setItem('portfolioTheme', selectedTheme)
    isDynamicFunnelEnabled
      ? routeToPage(PageName.SelectPortfolioTheme, ActionType.Continue)
      : props.route({ from: FlowRoute.SelectPortfolioTheme, action: 'next' })
  }

  const handleLearnMore = async () => {
    isDynamicFunnelEnabled
      ? routeToPage(PageName.SelectPortfolioTheme, ActionType.MoreInfo)
      : props.route({ from: FlowRoute.SelectPortfolioTheme, action: 'learn_more' })
  }

  useEffect(() => {
    const recommendedRisk = allPortfoliosData?.recommendedPortfolio?.name
    if (recommendedRisk === 'Conservative' && props.flow !== 'laterOnboarding') {
      localStorage.setItem('portfolioTheme', PortfolioThemes.CORE)
      props.route({
        from: FlowRoute.SelectPortfolioTheme,
        action: 'next',
      })
    }
  }, [allPortfoliosData])

  return (
    <>
      {loading ? (
        <SpinnerContainer>
          <Spinner />
        </SpinnerContainer>
      ) : (
        <>
          <Nav
            back={isDynamicFunnelEnabled && isBackEnabled(PageName.SelectPortfolioTheme)}
            hideProgressBar={true}
          />
          <MediaQuery maxWidth={themeValues.breakpoints.sm}>
            <Container>
              <StyledMobileCard>
                <Content>
                  <StyledHeading>Choose my portfolio theme</StyledHeading>
                  <ThemeContainer>
                    <ThemeCardButton
                      data-testid={TestIDs.SELECT_PORTFOLIO_THEME.CORE_BUTTON}
                      isActive={isCoreCardActive}
                      onClick={() => handleThemeSelectClick(PortfolioThemes.CORE)}
                    >
                      <ThemeCardTextContainer>
                        <Heading2>
                          {getRemoteString({
                            key: 'portfolio_theme.core.title',
                          })}
                        </Heading2>
                        <StyledBody>
                          {getRemoteString({
                            key: coreDetails,
                          })}
                        </StyledBody>
                      </ThemeCardTextContainer>
                      <CoreIconContainer>
                        {isCoreCardActive ? (
                          <AcornsIcon
                            icon={AcornsIcon.Icon.CheckmarkGreen}
                            width="22px"
                            height="22px"
                          />
                        ) : (
                          <AcornsIcon
                            icon={AcornsIcon.Icon.HollowCircle}
                            width="22px"
                            height="22px"
                          />
                        )}
                      </CoreIconContainer>
                    </ThemeCardButton>
                    <ThemeCardButton
                      data-testid={TestIDs.SELECT_PORTFOLIO_THEME.SUSTAINABLE_BUTTON}
                      isActive={isSustainableCardActive}
                      onClick={() => handleThemeSelectClick(PortfolioThemes.SUSTAINABLE)}
                    >
                      <ThemeCardTextContainer>
                        <Heading2>
                          {getRemoteString({
                            key: 'portfolio_theme.sustainable.title',
                          })}
                        </Heading2>
                        <StyledSustainableMainBody>
                          <StyledBody>
                            <StyledRemoteString
                              stringKey={sustainableDetails}
                              modifiers={[{ bold: 600 }, { bold: 600 }, { bold: 600 }]}
                            />
                          </StyledBody>
                          <SustainableIconContainer>
                            {isSustainableCardActive ? (
                              <AcornsIcon
                                icon={AcornsIcon.Icon.CheckmarkGreen}
                                width="22px"
                                height="22px"
                              />
                            ) : (
                              <AcornsIcon
                                icon={AcornsIcon.Icon.HollowCircle}
                                width="22px"
                                height="22px"
                              />
                            )}
                          </SustainableIconContainer>
                        </StyledSustainableMainBody>
                        <LearnMoreLink
                          data-testid={TestIDs.SELECT_PORTFOLIO_THEME.LEARN}
                          onClick={() => handleLearnMore()}
                        >
                          <StyledInfoNote
                            title={getRemoteString({
                              key: 'portfolio.theme.infonote.cta',
                            })}
                            callToActionLabel="Learn more"
                          />
                        </LearnMoreLink>
                      </ThemeCardTextContainer>
                    </ThemeCardButton>
                  </ThemeContainer>
                  <Separator mobileCtaSeparator extraCtaMargin />
                  <CTAContainer>
                    <NextButton
                      data-testid={TestIDs.SELECT_PORTFOLIO_THEME.NEXT}
                      disabled={isSelectedThemeEmpty}
                      onPress={() => handleContinue()}
                    >
                      Next
                    </NextButton>
                  </CTAContainer>
                </Content>
              </StyledMobileCard>
            </Container>
          </MediaQuery>
          <MediaQuery minWidth={themeValues.breakpoints.sm + 1}>
            <Container>
              <Content>
                <StyledHeading>Choose my portfolio theme</StyledHeading>
                <ThemeContainer>
                  <ThemeCardButton
                    data-testid={TestIDs.SELECT_PORTFOLIO_THEME.CORE_BUTTON}
                    isActive={isCoreCardActive}
                    onClick={() => handleThemeSelectClick(PortfolioThemes.CORE)}
                  >
                    <ThemeCardTextContainer>
                      <Heading2>
                        {getRemoteString({
                          key: 'portfolio_theme.core.title',
                        })}
                      </Heading2>
                      <StyledBody>
                        {getRemoteString({
                          key: coreDetails,
                        })}
                      </StyledBody>
                    </ThemeCardTextContainer>
                    <CoreIconContainer>
                      {isCoreCardActive ? (
                        <AcornsIcon
                          icon={AcornsIcon.Icon.CheckmarkGreen}
                          width="22px"
                          height="22px"
                        />
                      ) : (
                        <AcornsIcon
                          icon={AcornsIcon.Icon.HollowCircle}
                          width="22px"
                          height="22px"
                        />
                      )}
                    </CoreIconContainer>
                  </ThemeCardButton>
                  <ThemeCardButton
                    data-testid={TestIDs.SELECT_PORTFOLIO_THEME.SUSTAINABLE_BUTTON}
                    isActive={isSustainableCardActive}
                    onClick={() => handleThemeSelectClick(PortfolioThemes.SUSTAINABLE)}
                  >
                    <ThemeCardTextContainer>
                      <Heading2>
                        {getRemoteString({
                          key: 'portfolio_theme.sustainable.title',
                        })}
                      </Heading2>
                      <StyledSustainableMainBody>
                        <StyledBody>
                          <StyledRemoteString
                            stringKey={sustainableDetails}
                            modifiers={[{ bold: 600 }, { bold: 600 }, { bold: 600 }]}
                          />
                        </StyledBody>
                        <SustainableIconContainer>
                          {isSustainableCardActive ? (
                            <AcornsIcon
                              icon={AcornsIcon.Icon.CheckmarkGreen}
                              width="22px"
                              height="22px"
                            />
                          ) : (
                            <AcornsIcon
                              icon={AcornsIcon.Icon.HollowCircle}
                              width="22px"
                              height="22px"
                            />
                          )}
                        </SustainableIconContainer>
                      </StyledSustainableMainBody>
                      <LearnMoreLink
                        data-testid={TestIDs.SELECT_PORTFOLIO_THEME.LEARN}
                        onClick={() => handleLearnMore()}
                      >
                        <StyledInfoNote
                          title={getRemoteString({
                            key: 'portfolio.theme.infonote.cta',
                          })}
                          callToActionLabel="Learn more"
                        />
                      </LearnMoreLink>
                    </ThemeCardTextContainer>
                  </ThemeCardButton>
                </ThemeContainer>
              </Content>
              <Separator />
              <Content>
                <NextButton
                  data-testid={TestIDs.SELECT_PORTFOLIO_THEME.NEXT}
                  disabled={isSelectedThemeEmpty}
                  onPress={() => handleContinue()}
                >
                  Next
                </NextButton>
              </Content>
            </Container>
          </MediaQuery>
        </>
      )}
    </>
  )
}

export default withFlowRouter(SelectPortfolioTheme)
